// src/pages/ProjectDashboardPage.tsx
/**
 * Project Dashboard Page
 * Uses shared Dashboard components (TabControl, FilterBar, DashboardGroups, InfoWindow)
 * while keeping ProjectHeader at the top.
 */

import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSocketConnectionId } from '@/contexts/SocketContext';
import { authStorage, dashboardStorage } from '@/lib/storage';
import { dashboardApi, MIN_COLUMN_WIDTH_PX, gridSizeToColumnWidth } from '@/services/dashboardApi';
import { useDashboardViewSync } from '@/hooks/useDashboardViewSync';
import { dashboardService } from '@/services/dashboardService';
import DuplicatedItemsModal from '@/components/DashboardGroups/modals/DuplicatedItemsModal';
import { removeMovedCardsFromSource } from '@/components/DashboardGroups/dashboardCache';
import { getSelectedCardTitles } from '@/lib/cardDragState';
import { ProjectHeader } from '@/components/Dashboard/ProjectHeader';
import TabControl from '@/components/TabControl/TabControl';
import { FilterBar } from '@/components/Filter/FilterBar';
import { SearchBar } from '@/components/Filter/SearchBar';
import {
  QuickFilters,
  type QuickFilter,
} from '@/components/Filter/QuickFilters';
import { DashboardGroups } from '@/components/DashboardGroups/DashboardGroups';
import {
  DashboardViewSelector,
  type DashboardViewType,
} from '@/components/Dashboard/DashboardViewSelector';
import { RefreshNotificationButton } from '@/components/Dashboard/RefreshNotificationButton';
import { useSocketRefreshButton, getMoveToTopConversationIds } from '@/hooks/useSocketRefreshButton';
import { ProjectDetailsView } from '@/components/Dashboard/ProjectDetailsView';
import { GalleryView } from '@/components/Dashboard/GalleryView';
import { FilesView } from '@/components/Dashboard/FilesView';
import { useInfoWindow } from '@/contexts/InfoWindowContext';
import { useDashboardData } from '@/hooks/useDashboardData';
import { filterService } from '@/services/filterService';
import { useFilterPersistence } from '@/hooks/useFilterPersistence';
import {
  isDashboardPersonal,
  setDashboardPersonal,
  getPersonalFilterEntry,
  updatePersonalFilterRequest,
} from '@/lib/personalFilterConfig';

import { searchHistory } from '@/lib/searchHistory';
import { CalendarView } from '@/components/CalendarView';
import { GridView } from '@/components/GridView/GridView';
import type { GridViewRef } from '@/components/GridView/types';
import Gantt from '@/components/Gantt/Gantt';
import type { GanttConfig } from '@/components/Gantt/Gantt';
import { PhaseDetails } from '@/components/PhaseDetails/PhaseDetails';
import type { PhaseDetailsRef } from '@/components/PhaseDetails/types';
import { PhaseDetailsV2 } from '@/components/PhaseDetailsV2/PhaseDetailsV2';
import type { PhaseDetailsV2Ref } from '@/components/PhaseDetailsV2/types';
import MEView, { type MEViewHandle } from '@/components/MEView';
import {
  getViewMeGeneralInfo,
  getViewMeDetailInfo,
  mapMeViewToMetrics,
  EMPTY_ME_METRICS,
} from '@/services/meViewService';
import type { TeamMember, MetricsData } from '@/components/MEView/types';
import {
  useImportFromExcel,
  IMPORT_EXCEL_DONE_EVENT,
  type ImportExcelDoneDetail,
} from '@/contexts/ImportFromExcelContext';
import type { TabControlConfig, ReactFunctions, Dashboard } from '@/components/TabControl/types';
import type { FilterOption } from '@/types/dashboard';
import type { DashboardGroupData } from '@/components/DashboardGroups/types';
import { dashboardGroupsService } from '@/components/DashboardGroups/service';
import { dashboardGroupService } from '@/services/dashboardGroupService';
import type { ConversationDetail } from '@/types/conversation';
import './ProjectDashboardPage.css';
import { ReportViewer } from '@/components/Reports/ReportViewer';
import CapacityTracker from '@/components/Dashboard/CapacityTracker/CapacityTracker';
import { conversationBus } from '@/lib/conversationBus';

/**
 * Reads the layers hidden via LayerTags and formats them for the API.
 * Mirrors NewsFeedSection: a comma-joined string, empty when nothing is hidden.
 * LayerTags writes this key on every toggle, then dispatches 'layersToHideChanged'.
 */
const readLayersToHide = (): string =>
{
  try
  {
    const arr = JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]');
    return Array.isArray(arr) && arr.length ? arr.join(',') : '';
  }
  catch
  {
    return '';
  }
};

/**
 * ProjectDashboardContent - Content for the Project Dashboard page (without layout shell)
 * Used inside MainLayout via React Router's Outlet
 */
export const ProjectDashboardContent: React.FC = () =>
{
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const projectId = urlProjectId ? parseInt(urlProjectId, 10) : 35;
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Shared dashboard — populated from "Copy link" state (internal nav) or query param (direct URL)
  const sharedDashboardId: number | null =
    (location.state as any)?.sharedDashboardId ??
    (searchParams.get('ProjectDashboardId') ? parseInt(searchParams.get('ProjectDashboardId')!, 10) : null);
  const [isSharedPublic, setIsSharedPublic] = useState<boolean | null>(null);

  useEffect(() =>
  {
    if (!sharedDashboardId) return;
    dashboardApi
      .getDashboardById(sharedDashboardId)
      .then(d => setIsSharedPublic(d?.IsPublic ?? false))
      .catch(() => setIsSharedPublic(false));
  }, [sharedDashboardId]);

  const { IsAdmin, PTOApprover, isBetaME, PersonId } = useAuth();
  const canSeeMeView = IsAdmin() || PTOApprover() || isBetaME();
  const socketConnectionId = useSocketConnectionId();
  const { openImport } = useImportFromExcel();

  // Ref to hold latest canSeeMeView value
  const canSeeMeViewRef = useRef<boolean>(canSeeMeView);
  useEffect(() =>
  {
    canSeeMeViewRef.current = canSeeMeView;
  }, [canSeeMeView]);

  // Dashboard state - TabControl handles loading and selection
  const [currentDashboard, setCurrentDashboard] = useState<Dashboard | null>(null);
  const [pinnedDashboards, setPinnedDashboards] = useState<Dashboard[]>([]);
  // Bump to force GridView reload after a completed import on this dashboard
  const [gridVersion, setGridVersion] = useState(0);
  // ME GridView version (currently not auto-incremented; grid updates via conversationBus)
  const [meGridVersion] = useState(0);

  // Filter state
  const [filter, setFilter] = useState<string>('');
  const [filterOptions, setFilterOptions] = useState<FilterOption[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterError, setFilterError] = useState<string | null>(null);

  // Search state
  const [searchText, setSearchText] = useState<string>('');

  // Quick filters state
  const [quickFiltersSelected, setQuickFiltersSelected] = useState<string[]>([]);
  const [selectedQuickFilters, setSelectedQuickFilters] = useState<QuickFilter[]>([]);
  const [filtersReady, setFiltersReady] = useState(false);

  // Derived state: reset filtersReady synchronously during the render when the active
  // dashboard changes. useLayoutEffect fires after DOM commit — too late to prevent
  // DashboardGroups from mounting with stale params and firing a spurious API call.
  const [prevDashboardIdForFiltersReady, setPrevDashboardIdForFiltersReady] = useState(
    currentDashboard?.DashboardId ?? null,
  );
  if ((currentDashboard?.DashboardId ?? null) !== prevDashboardIdForFiltersReady)
  {
    setPrevDashboardIdForFiltersReady(currentDashboard?.DashboardId ?? null);
    if (filtersReady) setFiltersReady(false);
  }

  // Derived state: reset project-scoped state synchronously during the render when projectId
  // changes (filtersSelected handled by useFilterPersistence via key change). A useEffect
  // runs after commit — by then DashboardGroups has already fired a request for the new
  // project with the previous dashboard and IsFilterPersonal=false but empty filters, which
  // the backend persists as the dashboard's Apply For All filters, wiping them.
  const [prevProjectId, setPrevProjectId] = useState(projectId);
  if (projectId !== prevProjectId)
  {
    setPrevProjectId(projectId);
    setCurrentDashboard(null);
    setPinnedDashboards([]);
    setFilter('');
    setSearchText('');
    setQuickFiltersSelected([]);
    setSelectedQuickFilters([]);
    setFiltersReady(false);
  }

  // View mode state
  const [columnWidth, setColumnWidth] = useState(350);
  const [dashboardViewTypeId, setDashboardViewTypeId] = useState<DashboardViewType>(1);
  const { persistDashboardView, persistDashboardViewDebounced } = useDashboardViewSync();
  const [pageSize] = useState(130);
  const [showAllGalleryImages, setShowAllGalleryImages] = useState(false);

  // ME View state
  const [meMetrics, setMeMetrics] = useState<MetricsData>(EMPTY_ME_METRICS);
  const [meTeamMembers, setMeTeamMembers] = useState<TeamMember[]>([]);
  const [meViewLoading, setMeViewLoading] = useState(false);
  const [meSelectedMember, setMeSelectedMember] = useState<TeamMember | null>(null);
  const meViewRequestRef = useRef<{ cancel: () => void } | null>(null);

  // Report viewer state
  const [reportViewerDashboard, setReportViewerDashboard] = useState<Dashboard | null>(null);

  // Gantt View state
  const [enableWeekend, setEnableWeekend] = useState(false);
  const [selectedLayerId, setSelectedLayerId] = useState(0);
  const [ganttLayers, setGanttLayers] = useState<{ LayerId: number; Name: string }[]>([]);

  // Derive cardViewMode from dashboardViewTypeId
  const cardViewMode: 'compact' | 'detailed' = dashboardViewTypeId === 2 ? 'detailed' : 'compact';

  // Increments each time the user clicks a tab (even the already-active one) to force DashboardGroups reload
  const [reloadTrigger, setReloadTrigger] = useState(0);

  // Detect if the current tab is the Details tab
  const isDetailsTab = currentDashboard?.FakeDashboardType === 1;

  /*
   * View types whose component owns its own scrollbar: Gantt (7), PhaseDetailsV2 (8),
   * PhaseDetails (9) and Grid (4). Their wrappers scroll internally, so leaving the outer
   * .project-dashboard-scrollable scrollable as well produced two vertical scrollbars. The
   * page-level scroll is suppressed for these view types only.
   */
  const SELF_SCROLLING_VIEW_TYPES = [4, 7, 8, 9];
  const isGridView =
    SELF_SCROLLING_VIEW_TYPES.includes(dashboardViewTypeId) && !isDetailsTab;

  // Refs for TabControl
  const reactFunctionsRef = useRef<ReactFunctions | null>(null);
  const gridExportRef = useRef<GridViewRef>(null);
  const meGridExportRef = useRef<GridViewRef>(null);
  const phaseDetailsExportRef = useRef<PhaseDetailsRef>(null);
  const phaseDetailsV2ExportRef = useRef<PhaseDetailsV2Ref>(null);
  const meViewRef = useRef<MEViewHandle>(null);
  const meSummaryExportFnRef = useRef<(() => Promise<void>) | null>(null);

  // Ref to background-refresh DashboardGroups from socket notifications
  const dashboardGroupsRefreshRef = useRef<((conversationIds?: number[]) => void) | null>(null);

  // Layers hidden via LayerTags. Only Gantt needs this plumbed through — DashboardGroups
  // reads C2_LayersToHide itself and listens for the same event.
  const [layersToHide, setLayersToHide] = useState<string>(readLayersToHide);

  useEffect(() =>
  {
    const handleLayersChanged = () =>
    {
      setLayersToHide(readLayersToHide());
    };

    window.addEventListener('layersToHideChanged', handleLayersChanged);
    return () => window.removeEventListener('layersToHideChanged', handleLayersChanged);
  }, []);

  // Check if current dashboard has dynamic groups (IsDefault=true)
  const hasDynamicGroups = useMemo(
    () => currentDashboard?.DashboardGroups?.some(g => g.IsDefault === true) ?? false,
    [currentDashboard],
  );

  // Track visible conversationIds for socket filtering (only when no dynamic groups)
  const [visibleConversationIds, setVisibleConversationIds] = useState<Set<number>>(new Set());

  // Socket refresh notifications
  const {
    hasPendingUpdates,
    pendingCount,
    pendingOrderDashboard,
    pendingDashboardList,
    triggerRefresh,
    clearConversationHighlight,
    pendingGroupIds,
    pendingConversationIds,
  } = useSocketRefreshButton({
    currentDashboardId: currentDashboard?.DashboardId ?? null,
    configValueId: 1,
    isProjectView: true,
    projectId,
    hasDynamicGroups,
    visibleConversationIds,
    onRefresh: updates => dashboardGroupsRefreshRef.current?.(getMoveToTopConversationIds(updates)),
    onRefreshDashboardList: () => reactFunctionsRef.current?.reloadDashboard(false),
    onRefreshDashboardOrder: () =>
      reactFunctionsRef.current?.reloadDashboard(false, undefined, true),
    onDashboardRemoved: () => reactFunctionsRef.current?.reloadDashboard(false),
  });

  // Filter persistence (UI chips + applied filters + localStorage)
  const {
    filtersSelected,
    appliedFilters,
    handleFilterSelect,
    handleFilterUnselect,
    handleApplyFilters,
    overrideFilters,
  } = useFilterPersistence(`C2_Filters_Project_${projectId}_${currentDashboard?.DashboardId ?? 0}`);

  // Apply For All is ON by default; a dashboard is only personal when explicitly stored.
  const [isApplyForAll, setIsApplyForAll] = useState(true);

  // Sync isApplyForAll from personalFilterConfig whenever the active dashboard changes.
  // When Apply For All is ON, also clear any personal localStorage filters immediately
  // so they are never sent to the API while the global filters API call is in-flight.
  useEffect(() =>
  {
    if (!currentDashboard?.DashboardId)
    {
      setIsApplyForAll(true);
      overrideFilters([]);
      return;
    }
    const applyForAll = !isDashboardPersonal(currentDashboard.DashboardId);
    setIsApplyForAll(applyForAll);
    if (applyForAll)
    {
      overrideFilters([]);
    }
  }, [currentDashboard?.DashboardId, overrideFilters]);

  // Fallback restoration for personal mode: if the chip storage key is empty
  // (e.g. cache cleared) but a personalFilterConfig entry with Filters exists,
  // restore from it — mirrors legacy's fallback to personalFilterConfig.request.Filters.
  useEffect(() =>
  {
    if (!currentDashboard?.DashboardId) return;
    if (!isDashboardPersonal(currentDashboard.DashboardId)) return;
    if (appliedFilters.length > 0) return; // chip storage already has data

    const entry = getPersonalFilterEntry(currentDashboard.DashboardId);
    if (!entry?.request?.Filters) return;

    try
    {
      const raw: any[] = JSON.parse(entry.request.Filters);
      if (!Array.isArray(raw) || raw.length === 0) return;
      const restored = raw.map(f => ({
        FieldName: String(f.Name ?? ''),
        Id: String(f.Value ?? ''),
        Name: String(f.Text ?? ''),
        IsCustomField: f.IsCustomField ?? false,
        FieldTypeId: f.FieldTypeId ?? null,
        CustomFields: f.CustomFields ?? null,
        ComparisonOperator: f.ComparisonOperator ?? null,
        isVisible: true,
      }));
      overrideFilters(restored);
    }
    catch { /* ignore malformed JSON */ }
  }, [currentDashboard?.DashboardId, appliedFilters.length, overrideFilters]);

  // Load global filters only when the accessed dashboard has Apply For All ON.
  // isDashboardPersonal is read synchronously (localStorage) so there is no
  // stale-state timing issue: the check is always accurate at call time.
  useEffect(() =>
  {
    if (!currentDashboard?.DashboardId) return;
    // Only fetch when this specific dashboard is configured as Apply For All
    if (isDashboardPersonal(currentDashboard.DashboardId)) return;

    const dashboardId = currentDashboard.DashboardId;
    let cancelled = false;

    dashboardService.getDashboardGlobalFilters(dashboardId)
      .then(raw =>
      {
        if (cancelled) return;
        // Discard if Apply For All was turned off while the request was in flight
        if (isDashboardPersonal(dashboardId)) return;

        const filterBarFilters: FilterOption[] = [];

        if (raw)
        {
          try
          {
            const parsed: any[] = JSON.parse(raw);
            if (Array.isArray(parsed))
            {
              for (const f of parsed)
              {
                if (f.isQuickFilter)
                {
                  // Quick filters are per-user — never apply them from shared filters
                  continue;
                }
                else
                {
                  filterBarFilters.push({
                    FieldName: f.Name,
                    Id: f.Value,
                    Name: f.Text,
                    IsCustomField: f.IsCustomField ?? false,
                    FieldTypeId: f.FieldTypeId ?? 0,
                    CustomFields: f.CustomFields ?? null,
                    ComparisonOperator: f.ComparisonOperator ?? null,
                    isVisible: true,
                  });
                }
              }
            }
          }
          catch { /* ignore malformed JSON */ }
        }

        overrideFilters(filterBarFilters);
        // Ungate DashboardGroups once shared FilterBar filters are applied
        setFiltersReady(true);
      })
      .catch(err =>
      {
        if (cancelled) return;
        console.error('Error loading global filters:', err);
        // Still ungate on error so the dashboard renders (with empty filters)
        setFiltersReady(true);
      });

    return () =>
    {
      cancelled = true;
    };
  }, [currentDashboard?.DashboardId, overrideFilters]);

  // Get openInfoWindow from context
  const { openInfoWindow } = useInfoWindow();

  // Use existing hook for project info and side menu
  const { loading, error, projectInfo } = useDashboardData(projectId);

  // Reset quick filter UI state when switching dashboards.
  // filtersReady is reset via derived state above (synchronously during render)
  // so this effect only handles QF chip visual state.
  useLayoutEffect(() =>
  {
    if (!currentDashboard) return;
    setQuickFiltersSelected([]);
    setSelectedQuickFilters([]);
  }, [currentDashboard?.DashboardId]);

  // Load filter options when filter text changes
  // True once the user has typed in the filter input
  const filterTouchedRef = useRef<boolean>(false);

  useEffect(() =>
  {
    const loadFilterOptions = async () =>
    {
      if (filter.length > 0) filterTouchedRef.current = true;
      // Below the search threshold, reload the full list instead of clearing it
      const searchTerm = filter.length >= 2 ? filter : null;
      // On first mount the full list is loaded by onFilterOpened instead
      if (!searchTerm && !filterTouchedRef.current) return;

      try
      {
        setFilterLoading(true);
        setFilterError(null);
        const options = await filterService.getFilterOptions(searchTerm, {
          IsFromProject: true,
          ConfigValueId: 1,
        });
        setFilterOptions(options);
      }
      catch
      {
        setFilterError('Error loading filters');
      }
      finally
      {
        setFilterLoading(false);
      }
    };

    const debounceTimeout = setTimeout(loadFilterOptions, 300);
    return () => clearTimeout(debounceTimeout);
  }, [filter]);

  // Load initial filter options when FilterIcon is clicked
  const handleFilterOpened = useCallback(async () =>
  {
    try
    {
      setFilterLoading(true);
      setFilterError(null);
      const options = await filterService.getFilterOptions(null, {
        IsFromProject: true,
        ConfigValueId: 1,
      });
      setFilterOptions(options);
    }
    catch
    {
      setFilterError('Error loading filters');
    }
    finally
    {
      setFilterLoading(false);
    }
  }, []);

  // FilterBar select → add to shared state only.
  // We do NOT sync QuickFilters here because syncing instantly triggers an API execution
  // before the user clicks "Filter".
  const handleFilterBarSelect = useCallback(
    (filterOption: FilterOption) =>
    {
      handleFilterSelect(filterOption);
    },
    [handleFilterSelect],
  );

  // FilterBar unselect → remove from shared state only.
  const handleFilterBarUnselect = useCallback(
    (filterOption: FilterOption) =>
    {
      handleFilterUnselect(filterOption);
    },
    [handleFilterUnselect],
  );

  // Delay syncing QuickFilters until user explicitly hits Apply Filters
  const handleProjectApplyFilters = useCallback((applyForAll: boolean) =>
  {
    setIsApplyForAll(applyForAll);

    // Persist the personal/shared preference: dashboards NOT in personalFilterConfig
    // are treated as Apply For All ON; only "personal" dashboards are stored.
    if (currentDashboard?.DashboardId)
    {
      setDashboardPersonal(currentDashboard.DashboardId, !applyForAll);

      // Save the current filter chips to personalFilterConfig.request so they can
      // be restored when the filter chip storage is cleared (matches legacy behavior
      // of setPersonalFilterConfig which saved the full request including Filters).
      if (!applyForAll)
      {
        const filtersToSave = filtersSelected.map(f => ({
          Name: f.FieldName,
          Value: f.Id,
          Text: f.Name,
          IsCustomField: f.IsCustomField,
          FieldTypeId: f.FieldTypeId ?? null,
          CustomFields: f.IsCustomField ? (f.CustomFields ?? null) : null,
          ComparisonOperator: f.ComparisonOperator ?? null,
          isChipFilter: !f.Id ? true : false,
        }));
        updatePersonalFilterRequest(currentDashboard.DashboardId, {
          Filters: JSON.stringify(filtersToSave),
          IsFilterPersonal: true,
        });
      }
    }

    handleApplyFilters();
  }, [handleApplyFilters, filtersSelected, appliedFilters, currentDashboard?.DashboardId]);

  // QuickFilter toggled → FilterBar chips are independent, no sync on deactivation
  const handleQuickFilterToggled = useCallback(
    (_fieldName: string, _isSelected: boolean) =>
    {
      // QF and FilterBar filters are tracked separately via isQuickFilter.
      // Deactivating a QF chip must not remove the FilterBar entry.
    },
    [],
  );

  // Handle selected quick filters changed.
  // For personal dashboards QuickFilters loading from storage is the final async step,
  // so we mark filters as ready here. For Apply For All dashboards we hold off until
  // the global filters fetch completes (see getDashboardGlobalFilters effect below) —
  // this prevents a spurious API call with empty params before global filters arrive.
  const handleSelectedQuickFiltersChanged = useCallback((filters: QuickFilter[]) =>
  {
    setSelectedQuickFilters(filters);
    const dashId = currentDashboard?.DashboardId;
    if (!dashId || isDashboardPersonal(dashId))
    {
      setFiltersReady(true);
    }
  }, [currentDashboard?.DashboardId]);

  // Sync view resolution variable
  const [viewSyncDashboardId, setViewSyncDashboardId] = useState<number | null>(null);

  // Refs to hold latest view/grid without stale closures
  const dashboardViewTypeIdRef = useRef<DashboardViewType>(dashboardViewTypeId);
  useEffect(() =>
  {
    dashboardViewTypeIdRef.current = dashboardViewTypeId;
  }, [dashboardViewTypeId]);
  const columnWidthRef = useRef<number>(columnWidth);
  useEffect(() =>
  {
    columnWidthRef.current = columnWidth;
  }, [columnWidth]);
  const pinnedDashboardsRef = useRef<Dashboard[]>([]);
  useEffect(() =>
  {
    pinnedDashboardsRef.current = pinnedDashboards;
  }, [pinnedDashboards]);
  const projectIdRef = useRef<number>(projectId);
  useEffect(() =>
  {
    projectIdRef.current = projectId;
  }, [projectId]);

  if (currentDashboard && currentDashboard.DashboardId !== viewSyncDashboardId)
  {
    setViewSyncDashboardId(currentDashboard.DashboardId);

    let newViewId: DashboardViewType = 1;
    let newGridSize = 350;
    let shouldSaveState = false;

    // PRIORITY: Check localStorage for user's last visited view on this dashboard
    const savedEntry = dashboardStorage.findByContext(
      projectIdRef.current,
      1,
      currentDashboard.DashboardId,
    );

    if (savedEntry?.GridSize)
    {
      newGridSize = Math.max(MIN_COLUMN_WIDTH_PX, savedEntry.GridSize);
    }
    else if (currentDashboard.GridSize)
    {
      newGridSize = gridSizeToColumnWidth(currentDashboard.GridSize);
    }

    if (savedEntry?.DashboardViewId)
    {
      // Use saved view from localStorage (user's last visit)
      newViewId = savedEntry.DashboardViewId as DashboardViewType;
    }
    else
    {
      // First-time visit: set default based on permissions
      if (canSeeMeViewRef.current)
      {
        newViewId = 11; // ME View for users with permissions
      }
      else
      {
        newViewId = 1; // Compact view (small cards) as fallback
      }
      shouldSaveState = true;
    }

    setColumnWidth(newGridSize);
    setDashboardViewTypeId(newViewId);

    if (shouldSaveState)
    {
      const pinned = pinnedDashboardsRef.current;
      const index = pinned.findIndex(d => d.DashboardId === currentDashboard.DashboardId);
      dashboardStorage.updateDashboard({
        projectId: projectIdRef.current,
        configValueId: 1,
        index: index >= 0 ? index : 0,
        dashboardId: currentDashboard.DashboardId,
        DashboardViewId: newViewId,
        GridSize: newGridSize,
        lastModified: Date.now(),
      });
    }
  }

  // Helper to save dashboard selection state to C2_currentDashboard
  // Uses projectIdRef so the callback itself never captures a stale projectId
  const saveDashboardState = useCallback(
    (dashboard: Dashboard, viewId: DashboardViewType, gridSize: number) =>
    {
      const pinned = pinnedDashboardsRef.current;
      const index = pinned.findIndex(d => d.DashboardId === dashboard.DashboardId);
      dashboardStorage.updateDashboard({
        projectId: projectIdRef.current,
        configValueId: 1,
        index: index >= 0 ? index : 0,
        dashboardId: dashboard.DashboardId,
        DashboardViewId: viewId,
        GridSize: gridSize,
        lastModified: Date.now(),
      });
    },
    [],
  );

  // Ref to latest saveDashboardState — prevents view-sync effect from re-firing when projectId changes
  const saveDashboardStateRef = useRef(saveDashboardState);
  useEffect(() =>
  {
    saveDashboardStateRef.current = saveDashboardState;
  }, [saveDashboardState]);

  // NOTE: Removed auto-save on dashboard change to prevent saving stale view state.
  // Dashboard state is saved when user explicitly changes view (handleViewChange/handleColumnWidthChange)
  // and when view is synced after dashboard switch (lines 307-329).

  // Update browser tab title when project changes
  useEffect(() =>
  {
    document.title = projectInfo?.Name || 'Project';
  }, [projectInfo]);

  // Save project visit to search history
  useEffect(() =>
  {
    if (projectInfo?.ProjectID && projectInfo?.Name)
    {
      searchHistory.addProjectVisit(projectInfo.ProjectID, projectInfo.Name);
    }
  }, [projectInfo]);

  // Save dashboard visit to search history with project name
  useEffect(() =>
  {
    if (currentDashboard?.DashboardId && currentDashboard?.Name && projectInfo?.Name)
    {
      searchHistory.addDashboardVisit(
        currentDashboard.DashboardId,
        currentDashboard.Name,
        projectInfo.ProjectID,
        projectInfo.Name,
      );
    }
  }, [currentDashboard, projectInfo]);

  // Reload GridView when an import finishes on this exact dashboard and view 4 is active
  useEffect(() =>
  {
    const handler = (e: Event) =>
    {
      const { dashboardId } = (e as CustomEvent<ImportExcelDoneDetail>).detail;
      if (dashboardId === currentDashboard?.DashboardId && dashboardViewTypeIdRef.current === 4)
      {
        setGridVersion(v => v + 1);
      }
    };
    window.addEventListener(IMPORT_EXCEL_DONE_EVENT, handler);
    return () => window.removeEventListener(IMPORT_EXCEL_DONE_EVENT, handler);
  }, [currentDashboard]);

  // Get API configuration
  const getApiConfig = useCallback(() =>
  {
    const token = authStorage.getAccessToken() || '';
    const apiUrl =
      import.meta.env.VITE_API_URL ||
      import.meta.env.VITE_JS_apiURL ||
      'http://localhost/Corgee.Web/';

    return {
      url: apiUrl,
      token: token,
      refreshToken: '',
      TenancyName: import.meta.env.VITE_TENANCY_NAME || 'cgbot',
    };
  }, []);

  // TabControl handlers
  const handleSetDashboards = useCallback(
    (
      _allDashboards: Dashboard[],
      pinned: Dashboard[],
      _notPinned: Dashboard[],
      current: Dashboard | undefined,
      _isInitial?: boolean,
    ) =>
    {
      setPinnedDashboards(pinned);
      if (current)
      {
        setCurrentDashboard(current);
      }
    },
    [],
  );

  const handleSetDashboard = useCallback((dashboard: Dashboard) =>
  {
    setCurrentDashboard(dashboard);
    setSearchText('');
    setReloadTrigger(prev => prev + 1);
  }, []);

  const handleAddDashboard = useCallback((_event: React.MouseEvent) => {}, []);

  const handleUpdateDashboard = useCallback(
    async (_event: React.MouseEvent, dashboard: Dashboard) =>
    {
      try
      {
        const updated = await dashboardApi.updateDashboard(dashboard);
        setCurrentDashboard(prev =>
          prev?.DashboardId === updated.DashboardId ? { ...prev, ...updated } : prev,
        );
        setPinnedDashboards(prev => prev.map(d =>
          d.DashboardId === updated.DashboardId ? { ...d, ...updated } : d,
        ));
      }
      catch (error)
      {
        console.error('Error updating dashboard:', error);
      }
    },
    [],
  );

  const handlePinDashboard = useCallback(
    (_event: React.MouseEvent, _dashboard: Dashboard) => {},
    [],
  );

  const handleDeleteDashboard = useCallback(
    (_event: React.MouseEvent, _dashboard: Dashboard) => {},
    [],
  );

  const handleOpenTabDashboard = useCallback((_event: React.MouseEvent, dashboard: Dashboard) =>
  {
    setCurrentDashboard(dashboard);
  }, []);

  const handleDashboardUpdated = useCallback((updated: Dashboard) =>
  {
    setCurrentDashboard(prev =>
      prev?.DashboardId === updated.DashboardId ? { ...prev, ...updated } : prev,
    );
    setPinnedDashboards(prev => prev.map(d =>
      d.DashboardId === updated.DashboardId ? { ...d, ...updated } : d,
    ));
  }, []);

  const attachReactFunctions = useCallback((functions: ReactFunctions) =>
  {
    reactFunctionsRef.current = functions;
  }, []);

  const shareDashboard = useCallback((_event: React.MouseEvent, _dashboard: Dashboard) => {}, []);
  const duplicateDashboard = useCallback(
    (_event: React.MouseEvent, _dashboard: Dashboard) => {},
    [],
  );
  const editDashboard = useCallback((_event: React.MouseEvent, _dashboard: Dashboard) => {}, []);
  const openEditDashboard = useCallback(
    (_event: React.MouseEvent, _dashboard: Dashboard, _flag?: boolean) => {},
    [],
  );
  const archiveDashboardGroup = useCallback(
    (_event: React.MouseEvent, _dashboard: Dashboard) => {},
    [],
  );
  const showReports = useCallback((_event: React.MouseEvent, dashboard: Dashboard) =>
  {
    setReportViewerDashboard(dashboard);
  }, []);

  const handleReportCreated = useCallback((_dashboardId: number, _reportId: number) =>
  {
    // The ReportViewer manages reportId internally; parent can refresh dashboards if needed
  }, []);
  const pinTabDashboard = useCallback((_event: React.MouseEvent, _dashboard: Dashboard) => {}, []);
  const loadingBarStart = useCallback(() => {}, []);
  const loadingBarComplete = useCallback(() => {}, []);
  const editCapacityTracker = useCallback(
    (_event: React.MouseEvent, _dashboard: Dashboard) => {},
    [],
  );

  const onDuplicated = useCallback((newDashboardId?: number) =>
  {
    if (newDashboardId !== undefined)
    {
      reactFunctionsRef.current?.reloadDashboard(false, newDashboardId);
    }
    else
    {
      reactFunctionsRef.current?.reloadDashboard(true);
    }
  }, []);

  // Drag-to-tab: move/copy selected cards to another dashboard tab
  const [dragTabNotification, setDragTabNotification] = useState<string | null>(null);
  const dragTabNotificationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ShowDragTabNotification = useCallback((message: string) =>
  {
    if (dragTabNotificationTimerRef.current)
    {
      clearTimeout(dragTabNotificationTimerRef.current);
    }
    setDragTabNotification(message);
    dragTabNotificationTimerRef.current = setTimeout(() => setDragTabNotification(null), 3000);
  }, []);

  interface PendingDragOp {
    targetDashboard: Dashboard;
    conversationIds: number[];
    sourceGroupIds: Map<number, number>;
    sourceDashboardId: number;
    isCopy: boolean;
  }
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const pendingDragOpRef = useRef<PendingDragOp | null>(null);

  // Group drag-to-tab duplicate confirmation
  interface PendingGroupDrop {
    targetDashboard: Dashboard;
    sourceGroupId: number;
    sourceDashboardId: number;
    isCopy: boolean;
  }
  const [groupDuplicateModalOpen, setGroupDuplicateModalOpen] = useState(false);
  const pendingGroupDropRef = useRef<PendingGroupDrop | null>(null);

  const ExecuteDragOp = useCallback(
    async (op: PendingDragOp, removeDuplicates: boolean) =>
    {
      try
      {
        await dashboardService.addDashboardGroupByParametersDrag({
          dashboardIdSendTo: op.targetDashboard.DashboardId,
          conversationIds: op.conversationIds.join(','),
          removeDuplicates,
          moveAll: removeDuplicates,
          dashboardIdCurrent: op.sourceDashboardId,
          isFromMoveTo: null,
          ParentConversationId: 0,
          dashboardGroupOrigin: 0,
          socketConnectionId: socketConnectionId || '',
        });
        if (!op.isCopy)
        {
          for (const convId of op.conversationIds)
          {
            const groupId = op.sourceGroupIds.get(convId) ?? 0;
            if (groupId !== 0)
            {
              await dashboardService.deleteDashboardGroupConversation({
                DashboardGroupId: groupId,
                ConversationId: convId,
                IsFromBookmark: false,
                layersToHide: 0,
                socketConnectionId: socketConnectionId || '',
              });
            }
          }
        }
        const uniqueGroupIds = new Set(op.sourceGroupIds.values());
        uniqueGroupIds.forEach(gid =>
        {
          if (gid !== 0)
          {
            dashboardService.updatePercentDashboardGroupConversation(gid).catch(() => {});
          }
        });
        const count = op.conversationIds.length;
        ShowDragTabNotification(op.isCopy ? `${count} items copied` : `${count} items moved`);
        if (!op.isCopy)
        {
          removeMovedCardsFromSource(
            op.sourceDashboardId,
            op.conversationIds.map(id => ({ conversationId: id, groupId: op.sourceGroupIds.get(id) })),
          );
          dashboardGroupsRefreshRef.current?.();
        }
      }
      catch (err)
      {
        // swallow — user already sees no feedback on silent failures
      }
    },
    [socketConnectionId, ShowDragTabNotification],
  );

  const HandleDuplicateModalOk = useCallback(() =>
  {
    setDuplicateModalOpen(false);
    const op = pendingDragOpRef.current;
    if (op)
    {
      ExecuteDragOp(op, false);
    }
  }, [ExecuteDragOp]);

  const HandleDuplicateMoveAll = useCallback(() =>
  {
    setDuplicateModalOpen(false);
    const op = pendingDragOpRef.current;
    if (op)
    {
      ExecuteDragOp(op, true);
    }
  }, [ExecuteDragOp]);

  const HandleDuplicateCancel = useCallback(() =>
  {
    setDuplicateModalOpen(false);
    pendingDragOpRef.current = null;
  }, []);

  const HandleCardDroppedOnTab = useCallback(
    async (
      targetDashboard: Dashboard,
      conversationId: number,
      sourceGroupId: number,
      sourceDashboardId: number,
      isCopy: boolean,
    ) =>
    {
      if (!currentDashboard)
      {
        return;
      }
      try
      {
        const selectedTitles = getSelectedCardTitles();
        const isMultiDrag =
          selectedTitles.length > 1 && selectedTitles.some(s => s.id === conversationId);
        let conversationIds: number[];
        let sourceGroupIds: Map<number, number>;
        if (isMultiDrag)
        {
          conversationIds = selectedTitles.map(s => s.id);
          sourceGroupIds = new Map(selectedTitles.map(s => [s.id, s.groupId ?? sourceGroupId]));
        }
        else
        {
          conversationIds = [conversationId];
          sourceGroupIds = new Map([[conversationId, sourceGroupId]]);
        }
        const isDuplicated = await dashboardService.getConversationsDuplicated(
          targetDashboard.DashboardId,
          null,
          conversationIds.join(','),
        );
        const op: PendingDragOp = { targetDashboard, conversationIds, sourceGroupIds, sourceDashboardId, isCopy };
        if (isDuplicated)
        {
          pendingDragOpRef.current = op;
          setDuplicateModalOpen(true);
        }
        else
        {
          ExecuteDragOp(op, false);
        }
      }
      catch (err)
      {
        // swallow — user already sees no feedback on silent failures
      }
    },
    [currentDashboard, ExecuteDragOp],
  );

  const ExecuteGroupDrop = useCallback(
    async (op: PendingGroupDrop) =>
    {
      try
      {
        await dashboardGroupsService.AddDashboardGroupByParametersValidateCompatibleItems(
          op.targetDashboard.DashboardId,
          op.sourceGroupId,
          '0',
          0,
          true,
          1,
          projectId ? Number(projectId) : undefined,
          0,
          op.sourceGroupId,
        );
        if (!op.isCopy)
        {
          await dashboardGroupService.deleteDashboardGroup(op.sourceGroupId, '');
          dashboardGroupsRefreshRef.current?.();
        }
        ShowDragTabNotification(op.isCopy ? 'Group copied' : 'Group moved');
      }
      catch (err)
      {
        console.error('Error moving/copying group:', err);
      }
    },
    [projectId, ShowDragTabNotification],
  );

  const HandleGroupDroppedOnTab = useCallback(
    async (
      targetDashboard: Dashboard,
      sourceGroupId: number,
      sourceDashboardId: number,
      isCopy: boolean,
    ) =>
    {
      if (!currentDashboard) return;
      try
      {
        const op: PendingGroupDrop = { targetDashboard, sourceGroupId, sourceDashboardId, isCopy };
        const hasDuplicates = await dashboardGroupsService.GetConversationsDuplicated(
          targetDashboard.DashboardId,
          sourceGroupId,
          '',
        );
        if (hasDuplicates)
        {
          pendingGroupDropRef.current = op;
          setGroupDuplicateModalOpen(true);
        }
        else
        {
          ExecuteGroupDrop(op);
        }
      }
      catch (err)
      {
        console.error('Error in group drop handler:', err);
      }
    },
    [currentDashboard, ExecuteGroupDrop],
  );

  // TabControl configuration - note isProjectDetail and projectId
  const tabControlConfig: TabControlConfig = {
    api: getApiConfig(),
    configValueId: 1,
    projectId: projectId,
    DashboardId: currentDashboard?.DashboardId,
    isOrganize: false,
    isBookmarks: false,
    isProjectDetail: true,
    isDashboardShared: !!sharedDashboardId,
    dashboardSharedId: sharedDashboardId ?? undefined,
    recycleBinDashboard: false,
    canEdit: true,
    isAdmin: IsAdmin(),
    webUrl: import.meta.env.VITE_WEB_URL || 'http://localhost/Corgee.WebClient/',
    functions: {
      setDashboards: handleSetDashboards,
      setDashboard: handleSetDashboard,
      addDashboard: handleAddDashboard,
      updateDashboard: handleUpdateDashboard,
      pinDashboard: handlePinDashboard,
      deleteDashboard: handleDeleteDashboard,
      OpenTabDashboard: handleOpenTabDashboard,
      attachReactFunctions: attachReactFunctions,
      shareDashboard,
      duplicateDashboard,
      editDashboard,
      openEditDashboard,
      archiveDashboardGroup,
      showReports,
      pinTabDashboard,
      loadingBarStart,
      loadingBarComplete,
      editCapacityTracker,
      onDuplicated,
      getSocketConnectionId: () => socketConnectionId,
      onCardDroppedOnTab: HandleCardDroppedOnTab,
      onGroupDroppedOnTab: HandleGroupDroppedOnTab,
      onDashboardUpdated: handleDashboardUpdated,
    },
  };

  // True when the active tab is the shared dashboard and it has no public access
  // (No pin icon is shown in ProjectDetails — handled by TabControl via isProjectDetail flag)
  const isCurrentSharedNoPermission =
    !!sharedDashboardId &&
    currentDashboard?.DashboardId === sharedDashboardId &&
    isSharedPublic === false;

  // DashboardGroups callbacks
  const handleDataLoaded = useCallback((groups: DashboardGroupData[]) =>
  {
    // Extract all visible conversationIds for socket filtering
    const ids = new Set<number>();
    groups.forEach(group =>
    {
      group.Conversations?.forEach(conv =>
      {
        if (conv.ConversationId)
        {
          ids.add(conv.ConversationId);
        }
      });
    });
    setVisibleConversationIds(ids);
  }, []);

  const handleGroupsError = useCallback((_error: Error) => {}, []);

  const handleCardMoved = useCallback(
    (_conversationId: number, _fromGroupId: number, _toGroupId: number) => {},
    [],
  );

  const handleOpenInfoWindow = useCallback(
    (conversation: ConversationDetail | number) =>
    {
      const id = typeof conversation === 'number' ? conversation : conversation.ConversationId;
      const conv = typeof conversation === 'object' ? conversation : undefined;
      clearConversationHighlight(id);
      openInfoWindow(id, undefined, conv);
    },
    [openInfoWindow, clearConversationHighlight],
  );

  // Apply persisted view and grid size to local state
  const applyDashboardView = useCallback(
    (dashboardId: number, viewType: DashboardViewType, apiGridSize: number) =>
    {
      setCurrentDashboard(prev => prev && prev.DashboardId === dashboardId ? {
        ...prev,
        DashboardViewTypeId: viewType,
        GridSize: apiGridSize,
      } : prev);

      setPinnedDashboards(prev => prev.map(d =>
        d.DashboardId === dashboardId
          ? { ...d, DashboardViewTypeId: viewType, GridSize: apiGridSize }
          : d,
      ));

      reactFunctionsRef.current?.updateDashboardView(dashboardId, viewType, apiGridSize);
    },
    [],
  );

  // Handle view change: update state, save to C2_currentDashboard, call API
  const handleViewChange = useCallback(
    (viewType: DashboardViewType) =>
    {
      setDashboardViewTypeId(viewType);
      if (!currentDashboard) return;
      const gridSize = columnWidthRef.current;
      saveDashboardState(currentDashboard, viewType, gridSize);

      persistDashboardView(currentDashboard.DashboardId, viewType, gridSize, apiGridSize =>
      {
        applyDashboardView(currentDashboard.DashboardId, viewType, apiGridSize);
      });
    },
    [currentDashboard, saveDashboardState, persistDashboardView, applyDashboardView],
  );

  // Persist column resize (debounced)
  const handleColumnWidthChange = useCallback(
    (newWidth: number) =>
    {
      setColumnWidth(newWidth);
      if (!currentDashboard) return;

      const viewType = dashboardViewTypeIdRef.current;
      saveDashboardState(currentDashboard, viewType, newWidth);

      persistDashboardViewDebounced(
        currentDashboard.DashboardId,
        viewType,
        newWidth,
        apiGridSize =>
        {
          applyDashboardView(currentDashboard.DashboardId, viewType, apiGridSize);
        },
      );
    },
    [currentDashboard, saveDashboardState, persistDashboardViewDebounced, applyDashboardView],
  );

  // Refs to latest filter/search values for ME View (declared here; synced after memos below)
  const meFiltersJsonRef = useRef<string>('[]');
  const meSearchTextRef = useRef<string>('');
  const meCustomFieldsRef = useRef<unknown[]>([]);
  const meExtraParamsRef = useRef<Record<string, any>>({});

  /** Builds the base request payload, adding Me for individual-member requests. */
  const buildMeViewRequest = useCallback(
    (me?: number) => ({
      DashboardId: currentDashboard!.DashboardId,
      PageNumber: 1,
      PageSize: 50,
      DashboardViewTypeId: 11 as const,
      Project: String(projectIdRef.current),
      SearchText: meSearchTextRef.current,
      Filters: meFiltersJsonRef.current,
      LayersToHide: '',
      CustomFields: meCustomFieldsRef.current,
      ...meExtraParamsRef.current,
      ...(me !== undefined ? { Me: me } : {}),
    }),
    [currentDashboard],
  );

  // Load ME View summary (team overview) for admin/PTOApprover
  const loadMeViewSummary = useCallback(
    (silent = false) =>
    {
      if (!currentDashboard) return;
      meViewRequestRef.current?.cancel();
      if (!silent)
      {
        setMeViewLoading(true);
      }
      const req = getViewMeGeneralInfo(buildMeViewRequest());
      meViewRequestRef.current = req;
      req.promise
        .then(members =>
        {
          setMeTeamMembers(members);
        })
        .catch(() => {})
        .finally(() =>
        {
          if (!silent)
          {
            setMeViewLoading(false);
          }
        });
    },
    [currentDashboard, buildMeViewRequest],
  );

  // Load ME View detail for a specific member (or current user for non-admins)
  const loadMeViewDetail = useCallback(
    (personId: number, silent = false) =>
    {
      if (!currentDashboard) return;
      if (!silent)
      {
        setMeViewLoading(true);
        setMeMetrics(EMPTY_ME_METRICS);
      }
      getViewMeDetailInfo(buildMeViewRequest(personId))
        .then(data =>
        {
          if (data?.MeView)
          {
            setMeMetrics(
              mapMeViewToMetrics(
                data.MeView,
                currentDashboard.StartDate ?? '',
                currentDashboard.DueDate ?? '',
              ),
            );
          }
        })
        .catch(() => {})
        .finally(() =>
        {
          if (!silent)
          {
            setMeViewLoading(false);
          }
        });
    },
    [currentDashboard, buildMeViewRequest],
  );

  // Me View initial load: admin/PTOApprover → summary; non-admin → own detail
  useEffect(() =>
  {
    if (dashboardViewTypeId !== 11 || !currentDashboard) return;

    // Clear ME View state when dashboard changes to prevent stale data
    setMeSelectedMember(null);
    setMeTeamMembers([]);
    setMeMetrics(EMPTY_ME_METRICS);

    // Load appropriate view based on user role
    if (IsAdmin() || PTOApprover())
    {
      loadMeViewSummary();
    }
    else if (isBetaME())
    {
      const personId = parseInt(PersonId(), 10);
      if (personId > 0) loadMeViewDetail(personId);
    }
    return () =>
    {
      meViewRequestRef.current?.cancel();
    };
  }, [dashboardViewTypeId, currentDashboard]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to conversationBus events when in ME View to sync InfoWindow changes
  useEffect(() =>
  {
    if (dashboardViewTypeId !== 11 || !currentDashboard) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const handleFieldUpdate = () =>
    {
      // Debounce to avoid multiple reloads if several fields are updated rapidly
      if (debounceTimer) clearTimeout(debounceTimer);

      debounceTimer = setTimeout(() =>
      {
        const currentMePersonId =
          meSelectedMember?.PersonId ??
          (isBetaME() && !(IsAdmin() || PTOApprover()) ? parseInt(PersonId(), 10) : 0);

        if (IsAdmin() || PTOApprover())
        {
          if (currentMePersonId > 0)
          {
            // Reload member detail silently (no skeleton)
            loadMeViewDetail(currentMePersonId, true);
          }
          else
          {
            // Reload team summary silently (no skeleton)
            loadMeViewSummary(true);
          }
        }
        else if (isBetaME() && currentMePersonId > 0)
        {
          // Reload own detail silently (no skeleton)
          loadMeViewDetail(currentMePersonId, true);
        }

        // Note: Grid auto-updates via conversationBus events (no full reload needed)
      }, 300);
    };

    // Subscribe to events that affect ME metrics
    const unsubFieldUpdated = conversationBus.on('field-updated', handleFieldUpdate);
    const unsubReplyAdded = conversationBus.on('reply-added', handleFieldUpdate);
    const unsubReplyUpdated = conversationBus.on('reply-updated', handleFieldUpdate);
    const unsubLayerUpdated = conversationBus.on('layer-updated', handleFieldUpdate);

    return () =>
    {
      if (debounceTimer) clearTimeout(debounceTimer);
      unsubFieldUpdated();
      unsubReplyAdded();
      unsubReplyUpdated();
      unsubLayerUpdated();
    };
  }, [
    dashboardViewTypeId,
    currentDashboard,
    meSelectedMember,
    IsAdmin,
    PTOApprover,
    isBetaME,
    PersonId,
    loadMeViewDetail,
    loadMeViewSummary,
  ]);

  const handleMeMemberClick = useCallback(
    (member: TeamMember) =>
    {
      setMeSelectedMember(member);
      loadMeViewDetail(member.PersonId);
    },
    [loadMeViewDetail],
  );

  const handleMeBackToSummary = useCallback(() =>
  {
    setMeSelectedMember(null);
    loadMeViewSummary();
  }, [loadMeViewSummary]);

  // Handler for "Show More Details" in ProjectHeader — activates the Details tab
  const handleShowMoreDetails = useCallback(() =>
  {
    if (reactFunctionsRef.current?.openDetails)
    {
      reactFunctionsRef.current.openDetails();
    }
  }, []);

  // Memoized Filters JSON string for the API
  const filtersJson = useMemo(() =>
  {
    const allFilters: any[] = [];
    // Deduplicate by display name (Text) — matches legacy setFilter behavior:
    // "if (requestConversation[i].Text === filter.Name) addTag = false"
    const seenText = new Set<string>();

    const push = (entry: any) =>
    {
      // Skip filters with no Value — matches legacy getFilters:
      // "if (filter.Value != undefined && filter.Value != null)"
      if (entry.Value == null || entry.Value === '') return;
      if (seenText.has(String(entry.Text))) return;
      seenText.add(String(entry.Text));
      allFilters.push(entry);
    };

    // QuickFilters first — take priority in dedup
    selectedQuickFilters.forEach(qf =>
    {
      push({
        Name: qf.FieldName,
        IsCustomField: qf.IsCustomField,
        FieldTypeId: qf.FieldTypeId,
        CustomFields: null,         // legacy setQuickFilters forces null
        ComparisonOperator: qf.ComparisonOperator,
        isChipFilter: null,         // legacy setQuickFilters forces null
        isSelected: true,
        Text: qf.Name,
        Value: qf.Id,
        isQuickFilter: true,
      });
    });

    // FilterBar / global filters — skipped if display name already present via QF
    appliedFilters.forEach(f =>
    {
      push({
        Name: f.FieldName,
        IsCustomField: f.IsCustomField,
        FieldTypeId: f.FieldTypeId,
        CustomFields: f.IsCustomField ? (f.CustomFields ?? null) : null,
        ComparisonOperator: f.ComparisonOperator ?? null,
        // legacy setFilter: isChipFilter = Id is null/undefined ? true : false
        isChipFilter: !f.Id ? true : false,
        isSelected: true,
        Text: f.Name,
        Value: f.Id,
        isQuickFilter: false,
      });
    });

    return JSON.stringify(allFilters);
  }, [selectedQuickFilters, appliedFilters]);

  // Memoized top-level filter properties for the API
  const extraFilterParams = useMemo(() =>
  {
    const extra: Record<string, any> = {};
    const cfSeen = new Set<string>();
    const customFields: any[] = [];

    const pushCf = (cf: { Name: string; FieldTypeId: number | null; Value: any; ComparisonOperator: any }) =>
    {
      const key = `${cf.Name}_${cf.FieldTypeId}_${String(cf.Value)}`;
      if (cfSeen.has(key)) return;
      cfSeen.add(key);
      customFields.push(cf);
    };

    // QuickFilters → top-level or CustomFields
    selectedQuickFilters.forEach(qf =>
    {
      if (qf.IsCustomField)
      {
        pushCf({
          Name: qf.FieldName,
          FieldTypeId: qf.FieldTypeId,
          Value: qf.Id,
          ComparisonOperator: qf.ComparisonOperator || null,
        });
      }
      else
      {
        const num = parseInt(String(qf.Id), 10);
        extra[qf.FieldName] = isNaN(num) ? qf.Id : num;
      }
    });

    // FilterBar / global filters — top-level keys are overwritten (last wins), custom fields deduped
    appliedFilters.forEach(f =>
    {
      if (f.IsCustomField)
      {
        pushCf({
          Name: f.FieldName,
          FieldTypeId: f.FieldTypeId,
          Value: f.Id,
          ComparisonOperator: f.ComparisonOperator || null,
        });
      }
      else
      {
        const num = parseInt(String(f.Id), 10);
        extra[f.FieldName] = isNaN(num) ? f.Id : num;
      }
    });

    if (customFields.length > 0)
    {
      extra.CustomFields = customFields;
    }

    extra.IsFilterPersonal = !isApplyForAll;
    return extra;
  }, [selectedQuickFilters, appliedFilters, isApplyForAll]);

  const memoizedSearchText = useMemo(() => searchText.trim(), [searchText]);

  // Keep ME View filter refs in sync so callbacks always read fresh values
  useEffect(() =>
  {
    meFiltersJsonRef.current = filtersJson;
  }, [filtersJson]);
  useEffect(() =>
  {
    meSearchTextRef.current = memoizedSearchText;
  }, [memoizedSearchText]);
  useEffect(() =>
  {
    meCustomFieldsRef.current = extraFilterParams.CustomFields ?? [];
    meExtraParamsRef.current = { ...extraFilterParams };
    delete meExtraParamsRef.current.CustomFields;
  }, [extraFilterParams]);

  const isDark = document.documentElement.classList.contains('dark');

  if (error)
  {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-100 dark:bg-[#2d2f2f]">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">!</div>
          <p className="text-gray-800 dark:text-[#a9a093] font-semibold">Error loading dashboard</p>
          <p className="text-gray-600 dark:text-[#6e6860] text-sm mt-2">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (loading)
  {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-100 dark:bg-[#2d2f2f]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600 dark:text-[#6e6860]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {dragTabNotification && (
        <div
          style={{
            position: 'fixed',
            top: '70px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            background: '#62aacb',
            color: '#fff',
            borderRadius: '8px',
            boxShadow: '0 3px 6px rgba(0,0,0,.2)',
            padding: '6px 20px',
            fontSize: '14px',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {dragTabNotification}
        </div>
      )}
      <DuplicatedItemsModal
        open={duplicateModalOpen}
        isCopy={pendingDragOpRef.current?.isCopy}
        onCancel={HandleDuplicateCancel}
        onMoveAll={HandleDuplicateMoveAll}
        onOk={HandleDuplicateModalOk}
      />
      <DuplicatedItemsModal
        open={groupDuplicateModalOpen}
        isCopy={pendingGroupDropRef.current?.isCopy}
        onCancel={() =>
        {
          setGroupDuplicateModalOpen(false);
          pendingGroupDropRef.current = null;
        }}
        onMoveAll={() =>
        {
          setGroupDuplicateModalOpen(false);
          const op = pendingGroupDropRef.current;
          if (op) ExecuteGroupDrop({ ...op, isCopy: false });
        }}
        onOk={() =>
        {
          setGroupDuplicateModalOpen(false);
          const op = pendingGroupDropRef.current;
          if (op) ExecuteGroupDrop(op);
        }}
      />
      {/* Scrollable area — ProjectHeader scrolls away, tabs stick */}
      <div
        className={
          isGridView
            ? 'project-dashboard-scrollable project-dashboard-scrollable--grid'
            : 'project-dashboard-scrollable'
        }
      >
        {/* Project Header — scrolls out of view */}
        <ProjectHeader
          projectInfo={projectInfo}
          projectId={projectId}
          onOpenInfoWindow={openInfoWindow}
          onShowMoreDetails={handleShowMoreDetails}
        />

        {/* Sticky wrapper — tabs + filters stay visible */}
        <div className="project-dashboard-sticky">
          {/* TabControl Section */}
          <div className="border-gray-200 dark:border-[#4a4c4c] rounded-lg bg-gray-50 dark:bg-[#333535]">
            <TabControl key={projectId} config={tabControlConfig} />
          </div>

          {/* Filters Section — hidden when Details tab is active */}
          {!isDetailsTab && (
            <div
              className="flex items-center justify-between gap-2 mt-1.5"
              style={{ paddingLeft: '15px', paddingRight: '15px', position: 'relative' }}
            >
              {/* Left side: back+name in ME detail, nothing in ME summary, filters otherwise */}
              {dashboardViewTypeId === 11 && meSelectedMember ? (
                <>
                  <button
                    className="meview-back-btn"
                    onClick={() => meViewRef.current?.backToSummary()}
                  >
                    ← Back
                  </button>
                  <span
                    className="meview-member-name"
                    style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}
                  >
                    {meSelectedMember.Name}
                  </span>
                </>
              ) : dashboardViewTypeId === 11 ? null : (
                <div className="flex items-center gap-2">
                  <FilterBar
                    key={`fb-${currentDashboard?.DashboardId ?? 0}`}
                    filter={filter}
                    onChangeFilter={setFilter}
                    loading={filterLoading}
                    filterOptions={filterOptions}
                    filtersSelected={filtersSelected}
                    error={filterError}
                    onFilterOpened={handleFilterOpened}
                    onFilterSelect={handleFilterBarSelect}
                    onFilterUnselect={handleFilterBarUnselect}
                    isApplyForAll={isApplyForAll}
                    onApplyFilters={handleProjectApplyFilters}
                  />
                  <SearchBar
                    key={`sb-${currentDashboard?.DashboardId ?? 0}`}
                    filterSearchCard={searchText}
                    onChangeSearchFilterCards={setSearchText}
                  />
                  <QuickFilters
                    dashboardId={currentDashboard?.DashboardId ?? null}
                    quickFiltersSelected={quickFiltersSelected}
                    onChangeQuickFiltersSelected={setQuickFiltersSelected}
                    onFilterToggled={handleQuickFilterToggled}
                    onSelectedFiltersChanged={handleSelectedQuickFiltersChanged}
                  />
                </div>
              )}
              <div className="flex items-center gap-1 ml-auto">
                {dashboardViewTypeId !== 3 &&
                  dashboardViewTypeId !== 4 &&
                  dashboardViewTypeId !== 5 &&
                  dashboardViewTypeId !== 6 &&
                  dashboardViewTypeId !== 7 &&
                  dashboardViewTypeId !== 9 &&
                  dashboardViewTypeId !== 8 &&
                  dashboardViewTypeId !== 10 &&
                  dashboardViewTypeId !== 11 && (
                  <input
                    type="range"
                    min="250"
                    max="800"
                    value={columnWidth}
                    onChange={e => handleColumnWidthChange(Number(e.target.value))}
                    className="w-24 h-1 custom-slider"
                    style={{ accentColor: '#62aacb' }}
                  />
                )}
                {dashboardViewTypeId === 6 && (
                  <label
                    className="flex items-center gap-1 text-xs cursor-pointer"
                    style={{
                      color: isDark ? '#6e6860' : 'rgba(0,0,0,0.6)',
                      fontSize: '10px',
                      paddingRight: '15px',
                      paddingTop: '7px',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={showAllGalleryImages}
                      onChange={e => setShowAllGalleryImages(e.target.checked)}
                      className="w-3 h-3"
                    />
                    Show All Attached Images
                  </label>
                )}
                {dashboardViewTypeId === 7 && (
                  <>
                    <label
                      className="flex items-center gap-1 text-xs cursor-pointer"
                      style={{
                        color: isDark ? '#6e6860' : 'rgba(0,0,0,0.6)',
                        fontSize: '10px',
                        paddingRight: '15px',
                        paddingTop: '7px',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={enableWeekend}
                        onChange={e => setEnableWeekend(e.target.checked)}
                        className="w-3 h-3"
                      />
                      Enable Weekend
                    </label>
                    <select
                      className="text-xs border rounded px-1 py-0.5"
                      style={{
                        color: isDark ? '#a9a093' : 'rgba(0,0,0,0.7)',
                        backgroundColor: isDark ? '#3a3c3c' : undefined,
                        borderColor: isDark ? '#4a4c4c' : undefined,
                      }}
                      value={selectedLayerId}
                      onChange={e => setSelectedLayerId(Number(e.target.value))}
                    >
                      {ganttLayers.map(layer => (
                        <option key={layer.LayerId} value={layer.LayerId}>
                          {layer.Name}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                {dashboardViewTypeId === 4 && (
                  <button
                    className="view-bar-btn"
                    title="Export to Excel"
                    onClick={() => gridExportRef.current?.exportToExcel()}
                  >
                    <i className="material-icons">cloud_download</i>
                    <span>Export</span>
                  </button>
                )}
                {dashboardViewTypeId === 11 && (
                  <button
                    className="view-bar-btn"
                    title="Export to Excel"
                    onClick={() =>
                    {
                      if (meGridExportRef.current)
                      {
                        meGridExportRef.current.exportToExcel();
                      }
                      else
                      {
                        meSummaryExportFnRef.current?.();
                      }
                    }}
                  >
                    <i className="material-icons">cloud_download</i>
                    <span>Export</span>
                  </button>
                )}
                {dashboardViewTypeId === 9 && (
                  <>
                    <div
                      style={{
                        lineHeight: '24px',
                        color: isDark ? '#6e6860' : 'rgba(0, 0, 0, 0.45)',
                      }}
                      className="view-bar-button enable-help-tooltip"
                      ht-name="Hierarchical cards are not shown in this view"
                    >
                      <i className="fa fa-exclamation-circle fa-2"></i>
                    </div>
                    <button
                      className="view-bar-btn"
                      title="Export to Excel"
                      onClick={() => phaseDetailsExportRef.current?.exportToExcel()}
                    >
                      <i className="material-icons">cloud_download</i>
                      <span>Export</span>
                    </button>
                  </>
                )}
                {dashboardViewTypeId === 8 && (
                  <button
                    className="view-bar-btn"
                    title="Export to Excel"
                    onClick={() => phaseDetailsV2ExportRef.current?.exportToExcel()}
                  >
                    <i className="material-icons">cloud_download</i>
                    <span>Export</span>
                  </button>
                )}
                {dashboardViewTypeId === 4 && (
                  <button
                    className="view-bar-btn"
                    title="Import from Excel"
                    onClick={() =>
                      currentDashboard &&
                      openImport({
                        dashboardId: currentDashboard.DashboardId,
                        dashboardGroups: currentDashboard.DashboardGroups ?? [],
                        configValueId: 1,
                        projectId: String(projectId),
                      })
                    }
                    disabled={!currentDashboard}
                  >
                    <i className="material-icons">cloud_upload</i>
                    <span>Import</span>
                  </button>
                )}
                <DashboardViewSelector
                  currentViewTypeId={dashboardViewTypeId}
                  onViewChange={handleViewChange}
                  showMeView={canSeeMeView}
                  showCapacityTracker={true}
                />
                <RefreshNotificationButton
                  hasPendingUpdates={hasPendingUpdates}
                  pendingCount={pendingCount}
                  onClick={triggerRefresh}
                  message={
                    pendingDashboardList
                      ? 'Your dashboards have changed, click here to refresh'
                      : pendingOrderDashboard
                        ? 'Your dashboards have been reordered, click here to refresh'
                        : undefined
                  }
                />
              </div>
            </div>
          )}
        </div>

        {/* Content area */}
        <div className="project-dashboard-content">
          {/* No-permission message for shared dashboard */}
          {isCurrentSharedNoPermission && (
            <div className="flex flex-col items-center justify-center gap-3 text-gray-400 mt-16">
              <i className="fa fa-lock" style={{ fontSize: '48px' }}></i>
              <p className="text-base">You don't have permission to view this dashboard.</p>
            </div>
          )}

          {/* Details View — shown when Details tab is active */}
          {!isCurrentSharedNoPermission && isDetailsTab && projectInfo && (
            <ProjectDetailsView projectInfo={projectInfo} projectId={projectId} />
          )}

          {/* CalendarView Section — shown when Calendar view and NOT on Details tab */}
          {!isCurrentSharedNoPermission &&
            !isDetailsTab &&
            currentDashboard &&
            filtersReady &&
            dashboardViewTypeId === 3 && (
            <div className="mt-2">
              <CalendarView
                key={`cal-project-${projectId}-${currentDashboard.DashboardId}-r${reloadTrigger}`}
                dashboardId={currentDashboard.DashboardId}
                configValueId={1}
                projectId={String(projectId)}
                filters={filtersJson}
                searchText={memoizedSearchText}
                layersToHide={layersToHide}
                extraFilterParams={extraFilterParams}
                onOpenInfoWindow={id => openInfoWindow(id)}
                onRegisterRefresh={fn =>
                {
                  dashboardGroupsRefreshRef.current = fn;
                }}
              />
            </div>
          )}

          {/* GridView Section — shown when Grid view is selected and NOT on Details tab */}
          {!isCurrentSharedNoPermission &&
            !isDetailsTab &&
            currentDashboard &&
            filtersReady &&
            dashboardViewTypeId === 4 && (
            <div className="flex-1 min-h-0 overflow-hidden mt-2 bg-[#f9f9f9] dark:bg-[#2d2f2f]">
              <GridView
                ref={gridExportRef}
                key={`grid-project-${projectId}-${currentDashboard.DashboardId}-v${gridVersion}-r${reloadTrigger}`}
                dashboardId={currentDashboard.DashboardId}
                dashboardName={currentDashboard.Name}
                projectId={String(projectId)}
                filters={filtersJson}
                searchText={memoizedSearchText}
                layersToHide={layersToHide}
                extraFilterParams={extraFilterParams}
                isActive={true}
                onOpenInfoWindow={handleOpenInfoWindow}
              />
            </div>
          )}

          {/* PhaseDetails Section — shown when Phase Details (9) and NOT on Details tab */}
          {!isCurrentSharedNoPermission &&
            !isDetailsTab &&
            currentDashboard &&
            filtersReady &&
            dashboardViewTypeId === 9 && (
            <div className="flex-1 min-h-0 overflow-hidden mt-2 bg-[#f9f9f9] dark:bg-[#2d2f2f]">
              <PhaseDetails
                ref={phaseDetailsExportRef}
                key={`phase-project-${projectId}-${currentDashboard.DashboardId}-r${reloadTrigger}`}
                dashboardId={currentDashboard.DashboardId}
                dashboardName={currentDashboard.Name}
                projectId={String(projectId)}
                filters={filtersJson}
                searchText={memoizedSearchText}
                layersToHide={layersToHide}
                extraFilterParams={extraFilterParams}
                isActive={true}
                onOpenInfoWindow={handleOpenInfoWindow}
              />
            </div>
          )}

          {/* PhaseDetailsV2 Section */}
          {!isCurrentSharedNoPermission &&
            !isDetailsTab &&
            currentDashboard &&
            filtersReady &&
            dashboardViewTypeId === 8 && (
            <div className="flex-1 min-h-0 overflow-hidden mt-2 bg-[#f9f9f9] dark:bg-[#2d2f2f]">
              <PhaseDetailsV2
                ref={phaseDetailsV2ExportRef}
                key={`phase-v2-project-${projectId}-${currentDashboard.DashboardId}-r${reloadTrigger}`}
                dashboardId={currentDashboard.DashboardId}
                dashboardName={currentDashboard.Name}
                projectId={String(projectId)}
                filters={filtersJson}
                searchText={memoizedSearchText}
                layersToHide={layersToHide}
                extraFilterParams={extraFilterParams}
                isActive={true}
                onOpenInfoWindow={handleOpenInfoWindow}
              />
            </div>
          )}

          {/* GalleryView Section — shown when Gallery view (6) and NOT on Details tab */}
          {!isCurrentSharedNoPermission &&
            !isDetailsTab &&
            currentDashboard &&
            filtersReady &&
            dashboardViewTypeId === 6 && (
            <div className="mt-2">
              <GalleryView
                key={`gallery-project-${projectId}-${currentDashboard.DashboardId}-r${reloadTrigger}`}
                dashboardId={currentDashboard.DashboardId}
                projectId={String(projectId)}
                filters={filtersJson}
                searchText={memoizedSearchText}
                extraFilterParams={extraFilterParams}
                onOpenInfoWindow={id => openInfoWindow(id)}
                showAllImages={showAllGalleryImages}
              />
            </div>
          )}

          {/* FilesView Section — shown when Files view (5) and NOT on Details tab */}
          {!isCurrentSharedNoPermission &&
            !isDetailsTab &&
            currentDashboard &&
            filtersReady &&
            dashboardViewTypeId === 5 && (
            <div className="mt-2">
              <FilesView
                key={`files-project-${projectId}-${currentDashboard.DashboardId}-r${reloadTrigger}`}
                dashboardId={currentDashboard.DashboardId}
                projectId={String(projectId)}
                filters={filtersJson}
                searchText={memoizedSearchText}
                extraFilterParams={extraFilterParams}
                onOpenInfoWindow={id => openInfoWindow(id)}
              />
            </div>
          )}

          {/* ME View Section — shown when Me View (11) is selected */}
          {!isCurrentSharedNoPermission &&
            !isDetailsTab &&
            currentDashboard &&
            dashboardViewTypeId === 11 && (
            <div className="flex-1 min-h-0 overflow-hidden mt-2">
              <MEView
                ref={meViewRef}
                key={`meview-project-${projectId}-${currentDashboard.DashboardId}-r${reloadTrigger}`}
                metrics={meMetrics}
                teamMembers={meTeamMembers}
                isLoading={meViewLoading}
                isAdmin={IsAdmin() || PTOApprover()}
                startDate={currentDashboard.StartDate ?? ''}
                dueDate={currentDashboard.DueDate ?? ''}
                onMemberClick={handleMeMemberClick}
                onBackToSummary={handleMeBackToSummary}
                onSummaryExportReady={fn =>
                {
                  meSummaryExportFnRef.current = fn;
                }}
                showMemberHeader={false}
                gridSlot={(() =>
                {
                  const mePersonId =
                      meSelectedMember?.PersonId ??
                      (isBetaME() && !(IsAdmin() || PTOApprover()) ? parseInt(PersonId(), 10) : 0);
                  if (!mePersonId) return null;
                  return (
                    <GridView
                      ref={meGridExportRef}
                      key={`meview-grid-${mePersonId}-v${meGridVersion}`}
                      dashboardId={currentDashboard.DashboardId}
                      dashboardName={currentDashboard.Name}
                      projectId={String(projectId)}
                      filters={filtersJson}
                      searchText={memoizedSearchText}
                      layersToHide={layersToHide}
                      extraFilterParams={extraFilterParams}
                      isActive={true}
                      me={mePersonId}
                      onOpenInfoWindow={handleOpenInfoWindow}
                    />
                  );
                })()}
              />
            </div>
          )}

          {/* GanttView Section — shown when Gantt view (7) is selected and NOT on Details tab */}
          {!isCurrentSharedNoPermission &&
            !isDetailsTab &&
            currentDashboard &&
            filtersReady &&
            dashboardViewTypeId === 7 && (
            <div className="gantt-wrapper gantt-container">
              <Gantt
                key={`gantt-project-${projectId}-${currentDashboard.DashboardId}-layer${selectedLayerId}-hide${layersToHide}-weekend${enableWeekend}-${filtersJson}-${memoizedSearchText}-r${reloadTrigger}`}
                dashboard={{
                  DashboardId: currentDashboard.DashboardId,
                  DashboardViewTypeId: 7,
                  LayersToHide: layersToHide,
                  PageSize: 40,
                  PageNumber: 1,
                  Filters: filtersJson,
                  SearchText: memoizedSearchText,
                  Project: String(projectId),
                  ...extraFilterParams,
                }}
                config={
                    {
                      apiUrl: getApiConfig().url,
                      authToken: getApiConfig().token,
                      selectedLayerId: selectedLayerId,
                      WorkWeekend: enableWeekend,
                      openInfoWindow: (conversationId: number) => openInfoWindow(conversationId),
                      loadGanttLayers: (layers: any[]) => setGanttLayers(layers),
                    } as GanttConfig
                }
              />
            </div>
          )}

          {/* CapacityTracker Section — shown when Capacity Tracker (10) is selected and NOT on Details tab */}
          {!isCurrentSharedNoPermission &&
            !isDetailsTab &&
            currentDashboard &&
            dashboardViewTypeId === 10 && (
            <div className="flex-1 min-h-0 overflow-auto mt-2">
              <CapacityTracker
                key={`capacity-tracker-${projectId}-${currentDashboard.DashboardId}-r${reloadTrigger}`}
                config={{
                  dashboardId: String(currentDashboard.DashboardId),
                  dashboard: currentDashboard as any,
                  canEdit: true,
                  functions: {
                    openInfoWindow: (item: any) => openInfoWindow(item.ConversationId),
                  },
                }}
                registerSaveFn={() => {}}
              />
            </div>
          )}

          {/* DashboardGroups Section — shown for board views (1, 2) only */}
          {!isCurrentSharedNoPermission &&
            !isDetailsTab &&
            currentDashboard &&
            filtersReady &&
            dashboardViewTypeId !== 3 &&
            dashboardViewTypeId !== 4 &&
            dashboardViewTypeId !== 5 &&
            dashboardViewTypeId !== 6 &&
            dashboardViewTypeId !== 7 &&
            dashboardViewTypeId !== 9 &&
            dashboardViewTypeId !== 8 &&
            dashboardViewTypeId !== 10 &&
            dashboardViewTypeId !== 11 && (
            <div className="flex-1 min-h-0 overflow-hidden mt-2">
              <DashboardGroups
                key={`project-${projectId}-${currentDashboard.DashboardId}`}
                dashboardId={currentDashboard.DashboardId}
                projectId={String(projectId)}
                projectName={projectInfo?.Name || ''}
                filters={filtersJson}
                searchText={memoizedSearchText}
                pageSize={pageSize}
                cardViewMode={cardViewMode}
                columnWidth={columnWidth}
                dashboardViewTypeId={dashboardViewTypeId}
                configValueId={1}
                extraFilterParams={extraFilterParams}
                hideProjectName
                onDataLoaded={handleDataLoaded}
                onError={handleGroupsError}
                onCardMoved={handleCardMoved}
                onOpenInfoWindow={handleOpenInfoWindow}
                onRegisterRefresh={fn =>
                {
                  dashboardGroupsRefreshRef.current = fn;
                }}
                pendingGroupIds={pendingGroupIds}
                pendingConversationIds={pendingConversationIds}
                reloadTrigger={reloadTrigger}
              />
            </div>
          )}
        </div>
      </div>

      {reportViewerDashboard && (
        <ReportViewer
          dashboard={reportViewerDashboard}
          projectId={projectId}
          onClose={() => setReportViewerDashboard(null)}
          onReportCreated={handleReportCreated}
          onReportDeleted={() => setReportViewerDashboard(null)}
        />
      )}
    </div>
  );
};

// Keep legacy export for backwards compatibility
export const ProjectDashboardPage = ProjectDashboardContent;
